import React from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import { SparklesIcon } from '@heroicons/react/16/solid';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import Utilities from '@util/Utilities';
import { RefreshPriceRecommendationButton } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/RefreshPriceRecommendationButton';
import SubmitMonthlyPricesButton from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/SubmitMonthlyPricesButton';
import { useMarketStateTools } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import { isReviewAndSubmitEnabled } from './PricingWorkflowActions.utilities';
import { usePricingRights } from '@util/PricingRightsHook';

// The pricing section of the calendar and the Date based table: the two actions of the recommendation
// engine for the displayed month. Update runs the recommendations again and shows the age of the newest
// run of the month, also a run that gave only invalid recommendations; Review & Submit opens the monthly
// submit and is disabled when no day is submittable.
const PricingWorkflowActions: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	calendarMonth: CalendarMonth,
	filter: MarketStateFilterInput,
	canSubmitMonthlyPrices: boolean
}> = ({ companyHotel, calendarMonth, filter, canSubmitMonthlyPrices }) => {
	const tools = useMarketStateTools();
	const latestRunDate = tools.marketStateSnapshot.latestRecommendationRunDate;
	const hint = latestRunDate === undefined || latestRunDate === null ? undefined : `· ${Utilities.timeDifference(latestRunDate)} ago`;
	const { canViewRecommendations } = usePricingRights(companyHotel);
	const canReviewAndSubmit = isReviewAndSubmitEnabled(canSubmitMonthlyPrices, tools.marketStateDailySnapshotStates,
		companyHotel, canViewRecommendations, Utilities.formatISODate(new Date()));

	return <div className="flex w-full items-center gap-x-1 sm:w-auto">
		<CaptionGroup caption="Recommendation engine" icon={<SparklesIcon className="h-3 w-3" />} className="w-full sm:w-auto">
			<div className="flex w-full items-center gap-x-2 sm:w-auto">
				<div className="relative flex flex-1 items-center sm:flex-none">
					<RefreshPriceRecommendationButton
						companyHotel={companyHotel}
						calendarMonth={calendarMonth}
						filter={filter}
						label="Update"
						hint={hint}
						state="white"
						size="small"
						fullWidth />
				</div>
				<div className="relative flex flex-1 items-center sm:flex-none">
					<SubmitMonthlyPricesButton
						companyHotel={companyHotel}
						calendarMonth={calendarMonth}
						label="Review & Submit"
						size="small"
						disabled={!canReviewAndSubmit}
						extraClassName="w-full" />
				</div>
			</div>
		</CaptionGroup>
	</div>;
};

export default PricingWorkflowActions;
